import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { List, LayoutGrid, FileText, TrendingUp, TrendingDown, Users, AlertCircle, Info } from 'lucide-react';
import { ValidationIssue } from '@/types/payroll-validator';
import { IssueDetailCard } from './IssueDetailCard';
import { CategoryIssueGroup } from './CategoryIssueGroup';

interface EmployeeIssuesExpandedProps {
  issues: ValidationIssue[];
}

type ViewMode = 'flat' | 'category';

export const EmployeeIssuesExpanded: React.FC<EmployeeIssuesExpandedProps> = ({ issues }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('flat');

  const getCategoryInfo = (category: string) => {
    const categoryMap: Record<
      string,
      { title: string; icon: React.ReactNode; color: string }
    > = {
      pension: {
        title: "Pension Compliance",
        icon: <TrendingUp className="h-5 w-5" />,
        color: "text-blue-600 bg-blue-50 border-blue-200",
      },
      tax: {
        title: "Tax Code Issues",
        icon: <FileText className="h-5 w-5" />,
        color: "text-green-600 bg-green-50 border-green-200",
      },
      ni: {
        title: "National Insurance",
        icon: <Users className="h-5 w-5" />,
        color: "text-purple-600 bg-purple-50 border-purple-200",
      },
      pay_id: {
        title: "HMRC Pay ID / RTI",
        icon: <AlertCircle className="h-5 w-5" />,
        color: "text-orange-600 bg-orange-50 border-orange-200",
      },
      pay_history: {
        title: "Pay History",
        icon: <TrendingDown className="h-5 w-5" />,
        color: "text-red-600 bg-red-50 border-red-200",
      },
      employee_info: {
        title: "Employee Information",
        icon: <Users className="h-5 w-5" />,
        color: "text-indigo-600 bg-indigo-50 border-indigo-200",
      },
      student_loan: {
        title: "Student Loan",
        icon: <FileText className="h-5 w-5" />,
        color: "text-yellow-600 bg-yellow-50 border-yellow-200",
      },
    };

    return (
      categoryMap[category] || {
        title: category,
        icon: <Info className="h-5 w-5" />,
        color: "text-gray-600 bg-gray-50 border-gray-200",
      }
    );
  };

  const groupIssuesByCategory = (issues: ValidationIssue[]) => {
    return issues.reduce((acc, issue) => {
      if (!acc[issue.category]) {
        acc[issue.category] = [];
      }
      acc[issue.category].push(issue);
      return acc;
    }, {} as Record<string, ValidationIssue[]>);
  };

  const sortedIssues = [...issues].sort((a, b) => {
    // Sort by severity: critical > warning > info
    const severityOrder = { critical: 0, warning: 1, info: 2 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });

  const issuesByCategory = groupIssuesByCategory(issues);
  const categoryKeys = Object.keys(issuesByCategory);

  if (issues.length === 0) {
    return (
      <Card className="p-6 text-center text-gray-500">
        No issues found for this employee
      </Card>
    );
  }

  return (
    <div className="space-y-4 p-4 bg-gray-50 border-t">
      {/* Toggle Button */}
      <div className="flex justify-end">
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <Button
            variant={viewMode === 'flat' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('flat')}
            className="rounded-r-none"
          >
            <List className="h-4 w-4 mr-2" />
            Flat List
          </Button>
          <Button
            variant={viewMode === 'category' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('category')}
            className="rounded-l-none"
          >
            <LayoutGrid className="h-4 w-4 mr-2" />
            By Category
          </Button>
        </div>
      </div>

      {/* Flat List View */}
      {viewMode === 'flat' && (
        <div className="space-y-2">
          {sortedIssues.map((issue) => (
            <IssueDetailCard key={issue.issue_id} issue={issue} />
          ))}
        </div>
      )}

      {/* Category View */}
      {viewMode === 'category' && (
        <div className="space-y-4">
          {categoryKeys.map((category) => {
            const categoryInfo = getCategoryInfo(category);
            return (
              <CategoryIssueGroup
                key={category}
                category={category}
                categoryInfo={categoryInfo}
                issues={issuesByCategory[category]}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

